/**
 * usePageVisibility Hook
 *
 * Wraps the Page Visibility API to track when the page is visible or hidden.
 * Useful for detecting when the user backgrounds the app, locks the screen,
 * or switches to another tab.
 *
 * @example
 * const { isVisible, visibilityState } = usePageVisibility()
 *
 * if (!isVisible && isUploading) {
 *   showPausedMessage()
 * }
 */

import { useState, useEffect } from 'react'

export interface UsePageVisibilityReturn {
  /**
   * Whether the page is currently visible
   */
  isVisible: boolean

  /**
   * Current visibility state from the API
   */
  visibilityState: DocumentVisibilityState
}

/**
 * Gets the correct property name for document.visibilityState with vendor prefixes
 */
function getVisibilityStateProperty(): keyof Document {
  if (typeof document === 'undefined') {
    return 'visibilityState'
  }

  if ('visibilityState' in document) {
    return 'visibilityState'
  }

  // Vendor prefixes
  if ('webkitVisibilityState' in document) {
    return 'webkitVisibilityState' as keyof Document
  }

  if ('mozVisibilityState' in document) {
    return 'mozVisibilityState' as keyof Document
  }

  if ('msVisibilityState' in document) {
    return 'msVisibilityState' as keyof Document
  }

  return 'visibilityState'
}

/**
 * Gets the correct event name for visibilitychange with vendor prefixes
 */
function getVisibilityChangeEvent(): string {
  if (typeof document === 'undefined') {
    return 'visibilitychange'
  }

  if ('visibilityState' in document) {
    return 'visibilitychange'
  }

  // Vendor prefixes
  if ('webkitVisibilityState' in document) {
    return 'webkitvisibilitychange'
  }

  if ('mozVisibilityState' in document) {
    return 'mozvisibilitychange'
  }

  if ('msVisibilityState' in document) {
    return 'msvisibilitychange'
  }

  return 'visibilitychange'
}

/**
 * Hook for Page Visibility API
 *
 * Tracks when the page is visible or hidden. The page becomes hidden when:
 * - User switches to another tab
 * - User minimizes the browser
 * - User locks the device screen
 * - User switches to another app (mobile)
 *
 * Supports vendor prefixes for older browsers.
 *
 * @returns Page visibility state
 */
export function usePageVisibility(): UsePageVisibilityReturn {
  const [visibilityState, setVisibilityState] = useState<{
    isVisible: boolean
    state: DocumentVisibilityState
  }>(() => {
    if (typeof document === 'undefined') {
      return {
        isVisible: true,
        state: 'visible' as DocumentVisibilityState
      }
    }

    const visibilityProp = getVisibilityStateProperty()
    const currentState = (document[visibilityProp] as DocumentVisibilityState) || 'visible'

    return {
      isVisible: currentState === 'visible',
      state: currentState
    }
  })

  useEffect(() => {
    if (typeof document === 'undefined') {
      return
    }

    const visibilityProp = getVisibilityStateProperty()
    const eventName = getVisibilityChangeEvent()

    /**
     * Handle visibility change events
     */
    const handleVisibilityChange = () => {
      const currentState = (document[visibilityProp] as DocumentVisibilityState) || 'visible'

      setVisibilityState({
        isVisible: currentState === 'visible',
        state: currentState
      })
    }

    // Listen for visibility changes
    document.addEventListener(eventName, handleVisibilityChange, false)

    return () => {
      document.removeEventListener(eventName, handleVisibilityChange)
    }
  }, [])

  return {
    isVisible: visibilityState.isVisible,
    visibilityState: visibilityState.state
  }
}
